/**
 * Makes the shopper choose a size on the product page instead of silently
 * buying whichever size loaded first.
 *
 * The variant picker still holds a real variant underneath, because the
 * picker builds every variant request from its checked radios; unchecking one
 * would break colour changes. The size row is shown as unselected with CSS
 * (keyed off <html data-size-pending>, which survives section re-renders) and
 * Add to Bag is held until a size is chosen.
 *
 * The server (layout/theme.liquid) trusts a ?variant= already in the URL as
 * "already chosen" -- deliberately, since that's also what lets the AJAX
 * fragment fetched right after a genuine size click correctly render that
 * size. But a URL can carry a size the shopper never actually confirmed: the
 * picker resolves a full variant (and used to write it into the URL) from
 * whatever's still checked, including this masked default, the moment ANY
 * other option like colour changes. So this file requires its own,
 * session-scoped confirmation the URL can't fake -- see requiresFreshPick().
 */

const SIZE_OPTION = /^size$/i;
const HINT_TEXT = 'Select a size to add to bag';
const root = document.documentElement;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isPending = () => root.dataset.sizePending === 'true';

function confirmedSizeKey() {
  const handle = mainPicker()?.dataset.productHandle;
  return handle ? `altior:size-confirmed:${handle}` : null;
}

function hasConfirmedSizeThisSession() {
  try {
    const key = confirmedSizeKey();
    return !!key && sessionStorage.getItem(key) === 'true';
  } catch {
    return false; // Storage blocked (private mode, etc.) -- fail safe to "not confirmed".
  }
}

function rememberSizeConfirmed() {
  try {
    const key = confirmedSizeKey();
    if (key) sessionStorage.setItem(key, 'true');
  } catch {
    /* Storage blocked -- the in-page pending flag still governs this tab's current view. */
  }
}

function mainPicker() {
  return (
    document.querySelector('variant-picker[data-template-product-match="true"]') ??
    document.querySelector('.product-details variant-picker') ??
    document.querySelector('variant-picker')
  );
}

/** @returns {HTMLInputElement[]} */
function sizeInputs() {
  const picker = mainPicker();
  if (!picker) return [];
  return Array.from(picker.querySelectorAll('fieldset input[type="radio"]')).filter(
    (input) => input instanceof HTMLInputElement && SIZE_OPTION.test(input.dataset.optionName ?? '')
  );
}

/** Add-to-bag controls for this page's product, not recommendation or quick-add cards. */
function isMainAddToCart(/** @type {Element} */ element) {
  return !element.closest('dialog, product-card, product-recommendations, quick-add-component, .resource-card');
}

function syncStickyLabel() {
  if (!isPending()) return;
  const label = document.querySelector('.sticky-add-to-cart__variant');
  if (!label) return;
  const others = Array.from(mainPicker()?.querySelectorAll('fieldset input:checked') ?? [])
    .filter((input) => input instanceof HTMLInputElement && !SIZE_OPTION.test(input.dataset.optionName ?? ''))
    .map((input) => /** @type {HTMLInputElement} */ (input).value);
  const text = [...others, 'Select size'].join(' / ');
  if (label.textContent !== text) label.textContent = text;
}

/**
 * Writes every currently checked option (including size) straight into the
 * sticky label. Needed only for the already-checked-radio click case below:
 * no `change` fires there, so nothing else re-renders the label with the
 * size that was just confirmed.
 */
function writeConfirmedStickyLabel() {
  const label = document.querySelector('.sticky-add-to-cart__variant');
  if (!label) return;
  const values = Array.from(mainPicker()?.querySelectorAll('fieldset input:checked') ?? [])
    .filter((input) => input instanceof HTMLInputElement)
    .map((input) => /** @type {HTMLInputElement} */ (input).value);
  if (values.length) label.textContent = values.join(' / ');
}

function promptForSize() {
  const fieldset = sizeInputs()[0]?.closest('fieldset');
  if (!(fieldset instanceof HTMLElement)) return;

  fieldset.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });

  let hint = fieldset.querySelector('.size-required__hint');
  if (!hint) {
    hint = document.createElement('p');
    hint.className = 'size-required__hint';
    hint.setAttribute('role', 'alert');
    fieldset.querySelector('legend')?.after(hint);
  }
  // Re-set the text so a second attempt is announced again.
  hint.textContent = '';
  requestAnimationFrame(() => {
    if (hint) hint.textContent = HINT_TEXT;
  });

  window.AltiorMotion?.shake(fieldset);
}

function confirmSize() {
  rememberSizeConfirmed();
  if (!isPending()) return;
  delete root.dataset.sizePending;
  stickyObserver.disconnect();
  document.querySelectorAll('.size-required__hint').forEach((hint) => hint.remove());
}

const stickyObserver = new MutationObserver(syncStickyLabel);

function init() {
  const inputs = sizeInputs();
  if (inputs.length < 2) return;

  // A ?variant= already in the URL is not proof the shopper picked a size --
  // see the file-level comment -- so this only trusts a same-tab-session
  // confirmation it recorded itself.
  if (hasConfirmedSizeThisSession()) return;

  root.dataset.sizePending = 'true';
  syncStickyLabel();
  const stickyBar = document.querySelector('[ref="stickyBar"]');
  if (stickyBar) stickyObserver.observe(stickyBar, { childList: true, subtree: true, characterData: true });
}

function isSizeInputInMainPicker(/** @type {EventTarget | null} */ target) {
  return (
    target instanceof HTMLInputElement &&
    SIZE_OPTION.test(target.dataset.optionName ?? '') &&
    target.closest('variant-picker') === mainPicker()
  );
}

// Choosing a size by hand, keyboard, or the Fit Finder all fire `change` on
// the radio, which is what actually drives the picker's own update (price,
// sticky label, morph) -- so let that run undisturbed.
document.addEventListener(
  'change',
  (event) => {
    if (isSizeInputInMainPicker(event.target)) confirmSize();
  },
  true
);

// A mouse/touch click on the radio that was already checked under the hood
// (the phantom pre-selected size, still masked as unselected) never flips
// its checked state, so `change` never fires and the picker never reacts --
// only handle that one case here, checking .checked before the browser's
// default action runs, or a genuine new pick would also confirm here first
// and disconnect the sticky-label observer before `change` gets to update it.
document.addEventListener(
  'click',
  (event) => {
    const input = event.target;
    if (!isSizeInputInMainPicker(input) || !(/** @type {HTMLInputElement} */ (input).checked)) return;
    confirmSize();
    writeConfirmedStickyLabel();
  },
  true
);

// Window capture runs before the sticky bar and product form handlers, so a
// held click never reaches them.
window.addEventListener(
  'click',
  (event) => {
    if (!isPending()) return;
    const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button') : null;
    if (!button || !isMainAddToCart(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    promptForSize();
  },
  true
);

window.addEventListener(
  'submit',
  (event) => {
    if (!isPending()) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component') || !isMainAddToCart(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    promptForSize();
  },
  true
);

init();
